import * as React from 'react'
import _AutoSizer from 'react-virtualized-auto-sizer'

type Size = { width: number; height: number }
type AutoSizerProps = {
  children: (size: Size) => React.ReactNode
}

const useIsoLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

export const AutoSizerViewport: React.FC<AutoSizerProps> = ({ children }) => {
  const [frameSize, setFrameSize] = React.useState<Size | null>(null)

  // Measure the iframe's viewport inside the frame (no extra DOM, no styling changes)
  useIsoLayoutEffect(() => {
    if (typeof window === 'undefined') return
    const target = document.documentElement
    if (!target) return

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect
      if (!cr) return
      setFrameSize({
        width: Math.round(cr.width),
        height: Math.round(cr.height),
      })
    })
    ro.observe(target)

    // Optional: if the host page posts exact visible size (e.g., when scaled)
    const onMsg = (e: MessageEvent) => {
      const d = e.data
      if (d && d.type === 'host-frame-size'
          && typeof d.width === 'number' && typeof d.height === 'number') {
        setFrameSize({ width: Math.round(d.width), height: Math.round(d.height) })
      }
    }
    window.addEventListener('message', onMsg)

    return () => {
      ro.disconnect()
      window.removeEventListener('message', onMsg)
    }
  }, [])

  // Keep the exact DOM structure produced by react-virtualized-auto-sizer
  return (
    <_AutoSizer>
      {({ width, height }) => {
        const w = frameSize?.width ?? width
        const h = frameSize?.height ?? height
        return children({ width: w, height: h })
      }}
    </_AutoSizer>
  )
}